import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { allSteps, getMainConfig } from '../../../../redux/stepsSlice';
import { getCompany } from '../../../../redux/companySlice';
import { getMaterial, updateMaterial } from '../../../../redux/materialSlice';
import { API_ENDPOINTS, ENDPOINTS } from '../../utils/endpoints';
import { http_put } from '../../utils/api_client';
import Button from '../../../atoms/button/Button';
import Collaborators from '../../../organisms/collaborators/Collaborators';

// Walkthrough Editor v2 — top bar (Phase 0 authoring redesign).
//
// Replaces the legacy Toolbar for the v2 editor. Matches the mockup:
//   back ‹ · Walkthroughs breadcrumb · editable title (pencil)
//   · collaborators · Saved status · Preview · Publish
// It also owns autosave (moved out of the old Toolbar) and surfaces its
// state as a passive "Saving…/Saved" indicator instead of a manual button.

const BackIc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
);
const PencilIc = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
);
const CheckIc = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);
const EyeIc = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
);
const BoltIc = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" /></svg>
);
const ExternalIc = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14L21 3" /></svg>
);

const WalkthroughTopBar = ({ id, companyName, materialName, previewMode, onTogglePreview }) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => getCompany(state));
  const material = useSelector((state) => getMaterial(state));
  const steps = useSelector((state) => allSteps(state));
  const config = useSelector((state) => getMainConfig(state));

  const [saveState, setSaveState] = useState('saved'); // 'saved' | 'saving'
  const [isPublishing, setIsPublishing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const firstRun = useRef(true);

  const title = material?.name || materialName || 'Untitled walkthrough';
  const [draftTitle, setDraftTitle] = useState(title);
  useEffect(() => { if (!editingTitle) setDraftTitle(title); }, [title, editingTitle]);

  const materialsUrl = company?.slug ? ENDPOINTS.VIEW_LEARNING_MATERIALS(company.slug) : '#';

  // Autosave — debounce 1s after any change to steps/config, and drive the
  // Saved/Saving indicator. Skips the initial mount so we don't flash "Saving".
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (id === null || id === undefined) return;
    setSaveState('saving');
    const t = setTimeout(() => {
      http_put(API_ENDPOINTS.PUT_WALKTHROUGH(id), { data: steps, config })
        .then(() => setSaveState('saved'))
        .catch(() => setSaveState('saved'));
    }, 1000);
    return () => clearTimeout(t);
  }, [steps, config, id]);

  const commitTitle = () => {
    setEditingTitle(false);
    const name = (draftTitle || '').trim();
    if (!name || name === title) { setDraftTitle(title); return; }
    dispatch(updateMaterial({ name }));
    const slug = material?.slug;
    if (slug) {
      http_put(API_ENDPOINTS.PUT_WALKTHROUGH_SETTINGS(slug), { name }).catch(() => {});
    }
  };

  const onTitleKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    if (e.key === 'Escape') { setDraftTitle(title); setEditingTitle(false); }
  };

  const handlePublish = () => {
    setIsPublishing(true);
    http_put(API_ENDPOINTS.PUBLISH_WALKTHROUGH(id))
      .catch(() => {})
      .finally(() => setIsPublishing(false));
  };

  // Preview is its own page: navigate between /edit and /preview.
  const goToMode = () => {
    if (!company?.slug || !material?.slug) return;
    window.location.href = previewMode
      ? ENDPOINTS.EDIT_WALKTHROUGH(company.slug, material.slug)
      : ENDPOINTS.PREVIEW_WALKTHROUGH(company.slug, material.slug);
  };

  // Full browser view — the clean published-page render, opened in a new tab.
  const openFullView = () => {
    if (!company?.slug || !material?.slug) return;
    window.open(ENDPOINTS.FULL_VIEW_WALKTHROUGH(company.slug, material.slug), '_blank', 'noopener');
  };

  return (
    <header className="un-we-top">
      <div className="un-we-top__left">
        <a className="un-we-top__back" href={materialsUrl} title="Back to walkthroughs" aria-label="Back to walkthroughs">
          <BackIc />
        </a>
        <a className="un-we-top__crumb" href={materialsUrl}>Walkthroughs</a>
        <span className="un-we-top__chev">/</span>
        {editingTitle ? (
          <input
            className="un-we-top__title-in"
            value={draftTitle}
            autoFocus
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={onTitleKey}
          />
        ) : (
          <button type="button" className="un-we-top__title" onClick={() => setEditingTitle(true)} title="Rename walkthrough">
            <span className="t">{title}</span>
            <span className="pen"><PencilIc /></span>
          </button>
        )}
      </div>

      <div className="un-we-top__right">
        <Collaborators />
        <span className={`un-we-top__save${saveState === 'saving' ? ' saving' : ''}`}>
          {saveState === 'saving' ? <span className="dot" /> : <CheckIc />}
          {saveState === 'saving' ? 'Saving…' : 'Saved'}
        </span>
        {previewMode && (
          <button type="button" className="un-we-top__preview" onClick={openFullView} title="Open the full-page learner view in a new tab">
            <ExternalIc /> Full view
          </button>
        )}
        <button type="button" className="un-we-top__preview" onClick={goToMode}>
          {previewMode ? <><PencilIc /> Edit</> : <><EyeIc /> Preview</>}
        </button>
        <Button className={'primary'} onClick={handlePublish} isLoading={isPublishing}>
          <BoltIc /> Publish
        </Button>
      </div>
    </header>
  );
};

export default WalkthroughTopBar;
